import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import { TextAreaField } from "@/components/common/text-field";
import {
  insolvencySchema,
  type InsolvencyAPIPayload,
  type InsolvencyFormData,
} from "@/schemas/users/insolvency";
import { zodResolver } from "@hookform/resolvers/zod";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Button,
  GroupTextField,
  Input,
  Switch,
  TextField,
  Avatar,
  AvatarFallback,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import {
  Controller,
  FormProvider,
  useForm,
  type SubmitHandler,
} from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import type { RegistryType } from "@/schemas/digital-files";
import type { InsolvencyType } from "@/types/entities/insolvencies";
import {
  useCreateInsolvency,
  useUpdateInsolvency,
} from "@/queries/users/insolvencies.api";
import { sanitizeDecimalAmount, stripTrailingDot } from "@/utils/number";

const AMOUNT_MAX_DECIMALS = 2;

const PAYMENT_DEADLINE_UNITS = [
  { value: "HOURS", label: "Horas" },
  { value: "DAYS", label: "Días" },
  { value: "WEEKS", label: "Semanas" },
  { value: "MONTHS", label: "Meses" },
];

interface InsolvenciesFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  registry?: RegistryType;
  insolvencyData?: InsolvencyType;
}

export const InsolvenciesFormModal = ({
  registry,
  isOpen,
  onClose,
  insolvencyData,
}: InsolvenciesFormModalProps) => {
  const { showToast } = useToast();
  const { mutateAsync: addInsolvency, isPending: isCreatePending } =
    useCreateInsolvency(registry?.id);
  const { mutateAsync: updateInsolvency, isPending: isEditPending } =
    useUpdateInsolvency(insolvencyData?.id || "");
  const isEditMode = !!insolvencyData;

  const cleanValues = {
    reason: "",
    reference: "",
    code: "",
    can_appeal: false,
    payment_deadline_value: undefined,
    payment_deadline_unit: "DAYS" as const,
    amount: 0,
    insolvencyable_id: registry?.id || "",
    insolvencyable_type: "expedient_base_registry" as const,
    status: "ACTIVE" as const,
    comments: "",
  };

  const defaultValues = useMemo(
    () => ({
      reason: insolvencyData?.reason || "",
      reference: insolvencyData?.reference || "",
      code: insolvencyData?.code || "",
      can_appeal: insolvencyData?.can_appeal ?? false,
      payment_deadline_value: insolvencyData?.payment_deadline_value ?? 0,
      payment_deadline_unit: insolvencyData?.payment_deadline_unit ?? "DAYS",
      amount: insolvencyData ? Number(insolvencyData.amount) : undefined,
      insolvencyable_id:
        insolvencyData?.insolvencyable_id || registry?.id || "",
      insolvencyable_type: "expedient_base_registry" as const,
      status: insolvencyData?.status || "ACTIVE",
      comments: insolvencyData?.comments?.[0]?.text || "",
    }),
    [insolvencyData, registry?.id],
  );

  const methods = useForm<InsolvencyFormData>({
    resolver: zodResolver(insolvencySchema),
    defaultValues,
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
    register,
  } = methods;

  // Local display string for the amount input so decimals and edits render
  // exactly as typed; the form still stores a number for validation/submit.
  const [amountInput, setAmountInput] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    reset(defaultValues);
    setAmountInput(
      defaultValues.amount === undefined ? "" : String(defaultValues.amount),
    );
  }, [defaultValues, isOpen, reset]);

  const canAppeal = watch("can_appeal");

  const handleClose = () => {
    reset(cleanValues);
    setAmountInput("");
    onClose();
  };

  const onSubmit: SubmitHandler<InsolvencyFormData> = async (data) => {
    if (isEditMode) {
      try {
        const updatePayload: Partial<InsolvencyAPIPayload> = {
          comments: data.comments || "",
          reason: data.reason || "",
          status: data.status || "ACTIVE",
        };
        await updateInsolvency(updatePayload);
        showToast({
          title: "Infracción actualizada",
          description: "La infracción ha sido actualizada exitosamente.",
          options: {
            type: "success",
            duration: 4000,
          },
        });
        handleClose();
      } catch (error) {
        console.error("Error updating insolvency:", error);
        showToast({
          title: "Error",
          description:
            "No se pudo actualizar la infracción. Por favor, intenta nuevamente.",
          options: {
            type: "error",
            duration: 4000,
          },
        });
      }
    } else {
      try {
        const apiPayload: InsolvencyAPIPayload = {
          ...(data.reason &&
            data.reason.trim() !== "" && { reason: data.reason }),
          ...(data.reference &&
            data.reference.trim() !== "" && { reference: data.reference }),
          ...(data.code && data.code.trim() !== "" && { code: data.code }),
          ...(data.comments &&
            data.comments.trim() !== "" && { comments: data.comments }),
          can_appeal: data.can_appeal,
          payment_deadline_value: data.payment_deadline_value,
          payment_deadline_unit: data.payment_deadline_unit,
          amount: data.amount,
          insolvencyable_id: data.insolvencyable_id,
          insolvencyable_type: "expedient_base_registry",
          status: data.status || "ACTIVE",
        };
        await addInsolvency(apiPayload);
        showToast({
          title: "Infracción creada",
          description: "La infracción ha sido creada exitosamente.",
          options: {
            customIcon: (
              <Avatar size="xs" shape="square">
                <AvatarFallback color="action" shape="square">
                  <Icon
                    type="fas"
                    name="sack-dollar"
                    className="text-action-quiet"
                  />
                </AvatarFallback>
              </Avatar>
            ),
            duration: 4000,
          },
        });
        handleClose();
      } catch (error) {
        console.error("Error creating insolvency:", error);
        showToast({
          title: "Error",
          description:
            "No se pudo crear la infracción. Por favor, intenta nuevamente.",
          options: {
            type: "error",
            duration: 4000,
          },
        });
      }
    }
  };

  let editButtonText = "Añadir";
  if (isEditMode) {
    editButtonText = isEditPending ? "Guardando..." : "Guardar cambios";
  } else if (isCreatePending) {
    editButtonText = "Guardando...";
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        type="modal"
        className="max-w-130 p-0 gap-0"
        showCloseButton={false}
      >
        <DialogHeader className="py-4 px-8 flex-row w-full justify-between items-center border-b border-neutral-silent">
          <DialogTitle className="text-h4 font-semibold text-heading">
            {isEditMode ? "Editar infracción" : "Añadir infracción"}
          </DialogTitle>
          <DialogPrimitive.Close asChild>
            <Button
              type="button"
              size="icon"
              variant="quiet"
              aria-label="Cerrar"
            >
              <Icon name="close" type="fas" />
            </Button>
          </DialogPrimitive.Close>
        </DialogHeader>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-3 px-8"
          >
            <div className="flex flex-col gap-4 overflow-y-auto max-h-[calc(90vh-110px)] py-2">
              <TextField
                label="Motivo"
                required
                error={errors.reason?.message}
                placeholder="Título de la infracción"
                disabled={isEditMode}
                {...register("reason")}
              />

              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Monto"
                    required
                    prefix="$"
                    error={errors.amount?.message}
                    placeholder="$0.00"
                    type="text"
                    inputMode="decimal"
                    name={field.name}
                    ref={field.ref}
                    onBlur={() => {
                      field.onBlur();
                      setAmountInput(stripTrailingDot);
                    }}
                    value={amountInput}
                    onChange={(e) => {
                      const sanitized = sanitizeDecimalAmount(
                        e.target.value,
                        AMOUNT_MAX_DECIMALS,
                      );
                      setAmountInput(sanitized);
                      field.onChange(
                        sanitized === "" ? undefined : Number(sanitized),
                      );
                    }}
                    disabled={isEditMode}
                  />
                )}
              />

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="can-appeal"
                  className="text-body-loud font-semibold"
                >
                  <span>Posibilidad de apelar infracción</span>
                  <span className="text-danger">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <Controller
                    name="can_appeal"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        id="can-appeal"
                        checked={value}
                        onCheckedChange={onChange}
                        disabled={isEditMode}
                      />
                    )}
                  />
                  <div className="flex items-start gap-2">
                    <p className="text-sm text-body-quiet">
                      {canAppeal ? "Sí" : "No es posible apelar la infracción"}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-body-quiet leading-xl">
                  Si está activo, el tramitador podrá apelar para anular su
                  infracción.
                </p>
              </div>
              <GroupTextField
                label="Plazo para realizar el pago"
                error={errors.payment_deadline_value?.message}
              >
                <Controller
                  name="payment_deadline_value"
                  control={control}
                  render={({ field: { onChange, value, ...rest } }) => {
                    const displayValue = value >= 0 ? value.toString() : "";
                    return (
                      <Input
                        placeholder="Escribe un número"
                        type="text"
                        group="left"
                        inputMode="numeric"
                        value={displayValue}
                        onChange={(e) => {
                          const numValue = Number.parseInt(e.target.value, 10);
                          onChange(numValue);
                        }}
                        disabled={isEditMode}
                        className="w-full py-[10.5px] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        {...rest}
                      />
                    );
                  }}
                />
                <Controller
                  name="payment_deadline_unit"
                  control={control}
                  render={({ field }) => (
                    <DropdownList
                      options={PAYMENT_DEADLINE_UNITS}
                      selectedValue={PAYMENT_DEADLINE_UNITS.find(
                        (unit) => unit.value === field.value,
                      )}
                      toggleSelection={(item) => {
                        field.onChange(item.value);
                      }}
                      disabled={isEditMode}
                      widthClassName="w-32"
                      heightClassName="h-43"
                      group="right"
                    />
                  )}
                />
              </GroupTextField>
              <p className="text-xs text-body-quiet -mt-4 leading-xl">
                Si no quieres poner un limite de tiempo para el pago deja este
                campo vacío. Si deseas que el pago sea insolvente de manera
                inmediata escribe el numero 0.
              </p>

              <TextAreaField
                label="Comentario"
                required
                error={errors.comments?.message}
                placeholder="Escribe un comentario..."
                rows={3}
                {...register("comments")}
              />
            </div>

            <DialogFooter className="pb-3 px-3">
              <div className="flex justify-end gap-3 w-full">
                <Button
                  type="button"
                  variant="tertiary"
                  size="default"
                  onClick={handleClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  size="default"
                  disabled={
                    !isValid || (isEditMode ? isEditPending : isCreatePending)
                  }
                  isLoading={isEditMode ? isEditPending : isCreatePending}
                >
                  {editButtonText}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
